import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';

import { api } from '@/shared/api/http';
import type { CursorPage, FileRef, IsoDateTime, UserRef } from '@/shared/api/types';
import type { CommentItem } from '@/shared/components/comments';
import type { ReactionSummary, ReactionType } from '@/shared/components/reactions/reactions';

/*
 * Knowledge base API (backend module `wiki`, route root /api/wiki — docs/modules/wiki.md).
 * Query keys start with the module id `wiki`.
 */

export type SpaceKind = 'company' | 'group';
export type GroupVisibility = 'open' | 'closed' | 'secret';

export interface SpacePermissions {
  canEdit: boolean;
  canManage: boolean;
  canDelete: boolean;
}

export interface SpaceGroup {
  id: string;
  name: string;
  avatarUrl: string | null;
  isProject: boolean;
  visibility: GroupVisibility;
}

export interface SpaceListItem {
  id: string;
  kind: SpaceKind;
  name: string;
  description: string;
  icon: string;
  group: SpaceGroup | null;
  pageCount: number;
  updatedAt: IsoDateTime;
  canEdit: boolean;
}

export interface SpaceDetail {
  id: string;
  kind: SpaceKind;
  name: string;
  description: string;
  icon: string;
  group: SpaceGroup | null;
  membersCanEdit: boolean;
  isArchived: boolean;
  editorUsers: UserRef[];
  editorDepartmentIds: string[];
  pageCount: number;
  createdBy: UserRef | null;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  permissions: SpacePermissions;
}

export interface TreeNode {
  id: string;
  parentId: string | null;
  sortKey: string;
  title: string;
  icon: string | null;
  isPublished: boolean;
  hasDraft: boolean;
  updatedAt: IsoDateTime;
}

export interface PageRef {
  id: string;
  title: string;
  icon: string | null;
}

export interface SpaceRef {
  id: string;
  kind: SpaceKind;
  name: string;
  icon: string;
  groupId: string | null;
}

export interface Cover {
  fileId: string | null;
  url: string | null;
  preset: string | null;
}

export interface PagePermissions {
  canEdit: boolean;
  canDelete: boolean;
  canComment: boolean;
  canManageSpace: boolean;
}

export interface PageDetail {
  id: string;
  space: SpaceRef;
  parentId: string | null;
  breadcrumbs: PageRef[];
  title: string;
  icon: string | null;
  cover: Cover | null;
  contentHtml: string;
  version: number;
  isPublished: boolean;
  publishedAt: IsoDateTime | null;
  publishedBy: UserRef | null;
  createdBy: UserRef;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  isFavorite: boolean;
  isWatching: boolean;
  watcherCount: number;
  commentCount: number;
  children: PageRef[];
  myDraft: { updatedAt: IsoDateTime; baseVersion: number } | null;
  permissions: PagePermissions;
}

export interface PageListItem {
  id: string;
  title: string;
  icon: string | null;
  space: SpaceRef;
  isPublished: boolean;
  version: number;
  updatedAt: IsoDateTime;
  updatedBy: UserRef | null;
  excerpt: string | null;
  /** Viewed / favorited / draft saved at (lists). */
  at: IsoDateTime | null;
}

export interface Draft {
  pageId: string;
  title: string;
  contentHtml: string;
  baseVersion: number;
  currentVersion: number;
  updatedAt: IsoDateTime;
}

export interface VersionListItem {
  number: number;
  title: string;
  author: UserRef;
  createdAt: IsoDateTime;
  summary: string | null;
  restoredFrom: number | null;
  textLength: number;
  isCurrent: boolean;
}

export interface Version {
  number: number;
  title: string;
  icon: string | null;
  contentHtml: string;
  author: UserRef;
  createdAt: IsoDateTime;
  summary: string | null;
  restoredFrom: number | null;
  isCurrent: boolean;
}

export interface PageFlags {
  isFavorite: boolean;
  isWatching: boolean;
  watcherCount: number;
}

export interface WikiComment extends CommentItem {
  attachments: FileRef[];
}

export interface CreateSpaceInput {
  name: string;
  description: string | null;
  icon: string | null;
  editorUserIds: string[];
  editorDepartmentIds: string[];
}

export interface UpdateSpaceInput {
  name?: string;
  description?: string;
  icon?: string;
  editorUserIds?: string[];
  editorDepartmentIds?: string[];
  membersCanEdit?: boolean;
}

export interface CreatePageInput {
  parentId?: string | null;
  afterId?: string | null;
  title?: string;
  icon?: string | null;
  contentHtml?: string;
  publish?: boolean;
  /** A template of the space or the portal: its title pattern, icon and text (variables filled in) are used. */
  templateId?: string;
}

/** PATCH: absent = unchanged, null = clear. */
export interface UpdatePageInput {
  title?: string;
  icon?: string | null;
  cover?: { fileId?: string | null; preset?: string | null } | null;
}

export interface MovePageInput {
  parentId: string | null;
  afterId?: string | null;
  beforeId?: string | null;
}

export interface MovePageToSpaceInput {
  spaceId: string;
  parentId?: string | null;
  afterId?: string | null;
}

export type TemplateScope = 'space' | 'portal';

/** The variables a template can use (`{{name}}`): filled in on the server when a page is created. */
export const TEMPLATE_VARIABLES = [
  'date',
  'time',
  'datetime',
  'year',
  'month',
  'author',
  'title',
  'space',
] as const;

export interface TemplateItem {
  id: string;
  scope: TemplateScope;
  spaceId: string | null;
  name: string;
  description: string;
  icon: string | null;
  titlePattern: string;
  variables: string[];
  updatedBy: UserRef | null;
  updatedAt: IsoDateTime;
  canManage: boolean;
  /** Only in the detail (null in lists). */
  contentHtml: string | null;
}

export interface SaveTemplateInput {
  name: string;
  description?: string;
  icon?: string | null;
  titlePattern?: string;
  contentHtml?: string;
  /** Create only: copy the title and published text of this page. */
  fromPageId?: string;
}

export interface TrashEntry {
  id: string;
  title: string;
  icon: string | null;
  /** Published pages that went with it (itself included). */
  pageCount: number;
  deletedBy: UserRef | null;
  deletedAt: IsoDateTime;
  purgeAt: IsoDateTime;
  /** The page it was under, when that page still exists. */
  parent: PageRef | null;
  canRestore: boolean;
  canPurge: boolean;
}

export interface TrashList {
  items: TrashEntry[];
  retentionDays: number;
  canEmpty: boolean;
}

export interface RestoreResult {
  pageId: string;
  parentId: string | null;
  movedToTop: boolean;
  pageCount: number;
}

export type ExportFormat = 'md' | 'docx' | 'pdf';

export interface SaveDraftInput {
  title: string;
  contentHtml: string;
  baseVersion: number;
}

export interface PublishInput {
  title: string;
  contentHtml: string;
  summary?: string | null;
  baseVersion: number;
  force?: boolean;
}

export const wikiKeys = {
  all: ['wiki'] as const,
  spaces: () => ['wiki', 'spaces'] as const,
  space: (id: string) => ['wiki', 'space', id] as const,
  groupSpace: (groupId: string) => ['wiki', 'group-space', groupId] as const,
  tree: (spaceId: string) => ['wiki', 'tree', spaceId] as const,
  pages: () => ['wiki', 'page'] as const,
  page: (id: string) => ['wiki', 'page', id] as const,
  draft: (id: string) => ['wiki', 'draft', id] as const,
  versions: (id: string) => ['wiki', 'versions', id] as const,
  version: (id: string, number: number) => ['wiki', 'version', id, number] as const,
  comments: (id: string) => ['wiki', 'comments', id] as const,
  trashes: () => ['wiki', 'trash'] as const,
  trash: (spaceId: string) => ['wiki', 'trash', spaceId] as const,
  templates: () => ['wiki', 'templates'] as const,
  spaceTemplates: (spaceId: string) => ['wiki', 'templates', 'space', spaceId] as const,
  portalTemplates: () => ['wiki', 'templates', 'portal'] as const,
  template: (id: string) => ['wiki', 'templates', 'one', id] as const,
  lists: () => ['wiki', 'list'] as const,
  favorites: () => ['wiki', 'list', 'favorites'] as const,
  recent: () => ['wiki', 'list', 'recent'] as const,
  drafts: () => ['wiki', 'list', 'drafts'] as const,
  updates: (spaceId?: string | null) => ['wiki', 'list', 'updates', spaceId ?? 'all'] as const,
  search: (query: string, spaceId?: string | null) => ['wiki', 'search', spaceId ?? 'all', query] as const,
};

const enc = encodeURIComponent;

/** The file name of a `Content-Disposition` header (`filename*=UTF-8''…` wins over `filename="…"`), or null. */
export function dispositionFileName(header: string | null | undefined): string | null {
  if (!header) return null;
  const extended = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/u.exec(header)?.[1];
  if (extended) {
    try {
      return decodeURIComponent(extended.trim());
    } catch {
      // Not valid percent-encoding: fall back to the plain name.
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/u.exec(header)?.[1];
  return plain ? plain.trim() : null;
}

export const wikiApi = {
  spaces: (signal?: AbortSignal) => api.get<SpaceListItem[]>('/wiki/spaces', { signal }),
  space: (id: string, signal?: AbortSignal) => api.get<SpaceDetail>(`/wiki/spaces/${enc(id)}`, { signal }),
  groupSpace: (groupId: string, signal?: AbortSignal) =>
    api.get<SpaceDetail>(`/wiki/groups/${enc(groupId)}/space`, { signal }),
  createSpace: (input: CreateSpaceInput) => api.post<SpaceDetail>('/wiki/spaces', input),
  updateSpace: (id: string, input: UpdateSpaceInput) =>
    api.put<SpaceDetail>(`/wiki/spaces/${enc(id)}`, input),
  deleteSpace: (id: string) => api.delete(`/wiki/spaces/${enc(id)}`),
  tree: (spaceId: string, signal?: AbortSignal) =>
    api.get<TreeNode[]>(`/wiki/spaces/${enc(spaceId)}/tree`, { signal }),

  page: (id: string, signal?: AbortSignal) => api.get<PageDetail>(`/wiki/pages/${enc(id)}`, { signal }),
  createPage: (spaceId: string, input: CreatePageInput) =>
    api.post<PageDetail>(`/wiki/spaces/${enc(spaceId)}/pages`, input),
  updatePage: (id: string, input: UpdatePageInput) => api.patch<PageDetail>(`/wiki/pages/${enc(id)}`, input),
  deletePage: (id: string) => api.delete(`/wiki/pages/${enc(id)}`),
  movePage: (id: string, input: MovePageInput) =>
    api.put<PageDetail>(`/wiki/pages/${enc(id)}/position`, input),
  movePageToSpace: (id: string, input: MovePageToSpaceInput) =>
    api.post<PageDetail>(`/wiki/pages/${enc(id)}/move-to-space`, input),
  /** The caller's draft, or undefined (204) when there is none. */
  draft: (id: string, signal?: AbortSignal) =>
    api.get<Draft | undefined>(`/wiki/pages/${enc(id)}/draft`, { signal }),
  saveDraft: (id: string, input: SaveDraftInput) => api.put<Draft>(`/wiki/pages/${enc(id)}/draft`, input),
  discardDraft: (id: string) => api.delete(`/wiki/pages/${enc(id)}/draft`),
  publish: (id: string, input: PublishInput) => api.post<PageDetail>(`/wiki/pages/${enc(id)}/publish`, input),

  versions: (id: string, signal?: AbortSignal) =>
    api.get<VersionListItem[]>(`/wiki/pages/${enc(id)}/versions`, { signal }),
  version: (id: string, number: number, signal?: AbortSignal) =>
    api.get<Version>(`/wiki/pages/${enc(id)}/versions/${number}`, { signal }),
  restore: (id: string, number: number) =>
    api.post<PageDetail>(`/wiki/pages/${enc(id)}/versions/${number}/restore`),

  favorites: (signal?: AbortSignal) => api.get<PageListItem[]>('/wiki/favorites', { signal }),
  recent: (signal?: AbortSignal) => api.get<PageListItem[]>('/wiki/recent', { query: { limit: 10 }, signal }),
  drafts: (signal?: AbortSignal) => api.get<PageListItem[]>('/wiki/drafts', { signal }),
  updates: (spaceId: string | null | undefined, limit: number, signal?: AbortSignal) =>
    api.get<PageListItem[]>('/wiki/updates', { query: { spaceId: spaceId ?? undefined, limit }, signal }),
  search: (query: string, spaceId: string | null | undefined, signal?: AbortSignal) =>
    api.get<PageListItem[]>('/wiki/search', {
      query: { q: query, spaceId: spaceId ?? undefined, limit: 30 },
      signal,
    }),
  setFavorite: (id: string, on: boolean) =>
    on
      ? api.put<PageFlags>(`/wiki/pages/${enc(id)}/favorite`)
      : api.delete<PageFlags>(`/wiki/pages/${enc(id)}/favorite`),
  setWatching: (id: string, on: boolean) =>
    on
      ? api.put<PageFlags>(`/wiki/pages/${enc(id)}/watch`)
      : api.delete<PageFlags>(`/wiki/pages/${enc(id)}/watch`),
  recordView: (id: string) => api.post(`/wiki/pages/${enc(id)}/views`),

  trash: (spaceId: string, signal?: AbortSignal) =>
    api.get<TrashList>(`/wiki/spaces/${enc(spaceId)}/trash`, { signal }),
  restoreFromTrash: (spaceId: string, pageId: string) =>
    api.post<RestoreResult>(`/wiki/spaces/${enc(spaceId)}/trash/${enc(pageId)}/restore`),
  purgeFromTrash: (spaceId: string, pageId: string) =>
    api.delete(`/wiki/spaces/${enc(spaceId)}/trash/${enc(pageId)}`),
  emptyTrash: (spaceId: string) => api.delete(`/wiki/spaces/${enc(spaceId)}/trash`),

  portalTemplates: (signal?: AbortSignal) => api.get<TemplateItem[]>('/wiki/templates', { signal }),
  spaceTemplates: (spaceId: string, signal?: AbortSignal) =>
    api.get<TemplateItem[]>(`/wiki/spaces/${enc(spaceId)}/templates`, { signal }),
  template: (id: string, signal?: AbortSignal) => api.get<TemplateItem>(`/wiki/templates/${enc(id)}`, { signal }),
  createTemplate: (spaceId: string | null, input: SaveTemplateInput) =>
    spaceId
      ? api.post<TemplateItem>(`/wiki/spaces/${enc(spaceId)}/templates`, input)
      : api.post<TemplateItem>('/wiki/templates', input),
  updateTemplate: (id: string, input: SaveTemplateInput) =>
    api.put<TemplateItem>(`/wiki/templates/${enc(id)}`, input),
  deleteTemplate: (id: string) => api.delete(`/wiki/templates/${enc(id)}`),

  /** The exported file and the name the server gave it (Markdown arrives as text, the others as binary). */
  exportPage: async (
    id: string,
    format: ExportFormat,
    subtree: boolean,
  ): Promise<{ blob: Blob; fileName: string | null }> => {
    let disposition = '';
    const body = await api.get<Blob | string>(`/wiki/pages/${enc(id)}/export`, {
      query: { format, subtree },
      onResponse: (response) => {
        disposition = response.headers.get('content-disposition') ?? '';
      },
    });
    const blob = typeof body === 'string' ? new Blob([body], { type: 'text/markdown;charset=utf-8' }) : body;
    return { blob, fileName: dispositionFileName(disposition) };
  },

  comments: (id: string, cursor: string | null, signal?: AbortSignal) =>
    api.get<CursorPage<WikiComment>>(`/wiki/pages/${enc(id)}/comments`, {
      query: { cursor: cursor ?? undefined, limit: 20 },
      signal,
    }),
  createComment: (id: string, bodyHtml: string, attachmentIds: string[]) =>
    api.post<WikiComment>(`/wiki/pages/${enc(id)}/comments`, { bodyHtml, attachmentIds }),
  updateComment: (id: string, commentId: string, bodyHtml: string, attachmentIds: string[]) =>
    api.put<WikiComment>(`/wiki/pages/${enc(id)}/comments/${enc(commentId)}`, { bodyHtml, attachmentIds }),
  deleteComment: (id: string, commentId: string) =>
    api.delete(`/wiki/pages/${enc(id)}/comments/${enc(commentId)}`),
  react: (id: string, commentId: string, type: ReactionType) =>
    api.post<ReactionSummary[]>(`/wiki/pages/${enc(id)}/comments/${enc(commentId)}/reactions`, { type }),
};

// ---- queries --------------------------------------------------------------------------------------

export function useSpaces() {
  return useQuery({ queryKey: wikiKeys.spaces(), queryFn: ({ signal }) => wikiApi.spaces(signal) });
}

export function useSpace(id: string | null | undefined) {
  return useQuery({
    queryKey: wikiKeys.space(id ?? ''),
    queryFn: ({ signal }) => wikiApi.space(id ?? '', signal),
    enabled: !!id,
  });
}

export function useGroupSpace(groupId: string) {
  return useQuery({
    queryKey: wikiKeys.groupSpace(groupId),
    queryFn: ({ signal }) => wikiApi.groupSpace(groupId, signal),
  });
}

export function useTree(spaceId: string | null | undefined) {
  return useQuery({
    queryKey: wikiKeys.tree(spaceId ?? ''),
    queryFn: ({ signal }) => wikiApi.tree(spaceId ?? '', signal),
    enabled: !!spaceId,
  });
}

/** A page; `keepPrevious` keeps showing the previous page's data (marked as placeholder) while the next one loads. */
export function usePage(id: string | null | undefined, options: { keepPrevious?: boolean } = {}) {
  return useQuery({
    queryKey: wikiKeys.page(id ?? ''),
    queryFn: ({ signal }) => wikiApi.page(id ?? '', signal),
    enabled: !!id,
    placeholderData: options.keepPrevious ? keepPreviousData : undefined,
  });
}

export function useVersions(id: string) {
  return useQuery({ queryKey: wikiKeys.versions(id), queryFn: ({ signal }) => wikiApi.versions(id, signal) });
}

export function useVersion(id: string, number: number | null) {
  return useQuery({
    queryKey: wikiKeys.version(id, number ?? 0),
    queryFn: ({ signal }) => wikiApi.version(id, number ?? 0, signal),
    enabled: number !== null && number > 0,
    staleTime: Infinity,
  });
}

export function useTrash(spaceId: string | null | undefined) {
  return useQuery({
    queryKey: wikiKeys.trash(spaceId ?? ''),
    queryFn: ({ signal }) => wikiApi.trash(spaceId ?? '', signal),
    enabled: !!spaceId,
    retry: false,
  });
}

/** The templates a page of the space can be created from: the space's own, then the portal's. */
export function useSpaceTemplates(spaceId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: wikiKeys.spaceTemplates(spaceId ?? ''),
    queryFn: ({ signal }) => wikiApi.spaceTemplates(spaceId ?? '', signal),
    enabled: !!spaceId && enabled,
  });
}

export function usePortalTemplates() {
  return useQuery({
    queryKey: wikiKeys.portalTemplates(),
    queryFn: ({ signal }) => wikiApi.portalTemplates(signal),
  });
}

export function useTemplate(id: string | null | undefined) {
  return useQuery({
    queryKey: wikiKeys.template(id ?? ''),
    queryFn: ({ signal }) => wikiApi.template(id ?? '', signal),
    enabled: !!id,
  });
}

export function useFavorites() {
  return useQuery({ queryKey: wikiKeys.favorites(), queryFn: ({ signal }) => wikiApi.favorites(signal) });
}

export function useRecent() {
  return useQuery({ queryKey: wikiKeys.recent(), queryFn: ({ signal }) => wikiApi.recent(signal) });
}

export function useDrafts() {
  return useQuery({ queryKey: wikiKeys.drafts(), queryFn: ({ signal }) => wikiApi.drafts(signal) });
}

export function useUpdates(spaceId: string | null | undefined, limit = 10) {
  return useQuery({
    queryKey: [...wikiKeys.updates(spaceId), limit],
    queryFn: ({ signal }) => wikiApi.updates(spaceId, limit, signal),
  });
}

export function useWikiSearch(query: string, spaceId?: string | null) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: wikiKeys.search(trimmed, spaceId),
    queryFn: ({ signal }) => wikiApi.search(trimmed, spaceId, signal),
    enabled: trimmed.length >= 2,
    staleTime: 30_000,
  });
}

// ---- cache helpers ----------------------------------------------------------------------------------

/** Everything a change of pages in a space can affect (tree, lists, the space's counters). */
export async function invalidateSpaceContent(queryClient: QueryClient, spaceId: string): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: wikiKeys.tree(spaceId) }),
    queryClient.invalidateQueries({ queryKey: wikiKeys.lists() }),
    queryClient.invalidateQueries({ queryKey: wikiKeys.spaces() }),
    queryClient.invalidateQueries({ queryKey: wikiKeys.space(spaceId) }),
    queryClient.invalidateQueries({ queryKey: ['wiki', 'search'] }),
  ]);
}

/** Stores a page returned by a mutation and refreshes what depends on it. */
export function storePage(queryClient: QueryClient, page: PageDetail): void {
  queryClient.setQueryData(wikiKeys.page(page.id), page);
  void invalidateSpaceContent(queryClient, page.space.id);
  void queryClient.invalidateQueries({ queryKey: wikiKeys.versions(page.id) });
}

// ---- mutations ----------------------------------------------------------------------------------------

export function useCreatePage(spaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePageInput) => wikiApi.createPage(spaceId, input),
    onSuccess: (page) => storePage(queryClient, page),
  });
}

export function useUpdatePage(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePageInput) => wikiApi.updatePage(id, input),
    onMutate: async (input) => {
      // Optimistic: title, icon and cover show at once.
      await queryClient.cancelQueries({ queryKey: wikiKeys.page(id) });
      const previous = queryClient.getQueryData<PageDetail>(wikiKeys.page(id));
      if (previous) {
        queryClient.setQueryData<PageDetail>(wikiKeys.page(id), {
          ...previous,
          ...(input.title !== undefined ? { title: input.title } : {}),
          ...(input.icon !== undefined ? { icon: input.icon } : {}),
          ...(input.cover !== undefined
            ? {
                cover: input.cover
                  ? {
                      fileId: input.cover.fileId ?? null,
                      url: previous.cover?.url ?? null,
                      preset: input.cover.preset ?? null,
                    }
                  : null,
              }
            : {}),
        });
      }
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(wikiKeys.page(id), context.previous);
    },
    onSuccess: (page) => storePage(queryClient, page),
  });
}

export function usePageFlag(id: string, flag: 'favorite' | 'watch') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (on: boolean) =>
      flag === 'favorite' ? wikiApi.setFavorite(id, on) : wikiApi.setWatching(id, on),
    onMutate: async (on) => {
      await queryClient.cancelQueries({ queryKey: wikiKeys.page(id) });
      const previous = queryClient.getQueryData<PageDetail>(wikiKeys.page(id));
      if (previous) {
        queryClient.setQueryData<PageDetail>(
          wikiKeys.page(id),
          flag === 'favorite'
            ? { ...previous, isFavorite: on }
            : {
                ...previous,
                isWatching: on,
                watcherCount: Math.max(
                  0,
                  previous.watcherCount + (on === previous.isWatching ? 0 : on ? 1 : -1),
                ),
              },
        );
      }
      return { previous };
    },
    onError: (_error, _on, context) => {
      if (context?.previous) queryClient.setQueryData(wikiKeys.page(id), context.previous);
    },
    onSuccess: (flags) => {
      const current = queryClient.getQueryData<PageDetail>(wikiKeys.page(id));
      if (current) queryClient.setQueryData<PageDetail>(wikiKeys.page(id), { ...current, ...flags });
      if (flag === 'favorite') void queryClient.invalidateQueries({ queryKey: wikiKeys.favorites() });
    },
  });
}
